import React from 'react';
import { Activity, Scan, HeartPulse, CheckCircle2, Clock, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { services, labInfo } from '../mockData';

const Services = () => {
  const serviceDetails = [
    {
      id: 1,
      icon: Activity,
      title: 'Digital X-Ray',
      image: services[2].image,
      description: 'Advanced digital X-ray imaging technology provides clear, high-resolution images with minimal radiation exposure.',
      benefits: [
        'Instant digital results',
        'Lower radiation dose',
        'High image quality',
        'Immediate diagnosis support'
      ],
      applications: [
        'Chest X-Ray',
        'Bone imaging',
        'Joint assessment',
        'Fracture detection'
      ]
    },
    {
      id: 2,
      icon: Scan,
      title: 'Dental X-Ray (OPG)',
      image: services[1].image,
      description: 'Orthopantomogram (OPG) provides a comprehensive view of your teeth, jaws, and surrounding structures in a single image.',
      benefits: [
        'Complete dental imaging',
        'Painless procedure',
        'Quick scanning',
        'Detailed jaw structure view'
      ],
      applications: [
        'Dental treatment planning',
        'Orthodontic assessment',
        'Wisdom tooth evaluation',
        'Jaw bone analysis'
      ]
    },
    {
      id: 3,
      icon: HeartPulse,
      title: 'ECG – 12 Channel',
      image: services[0].image,
      description: 'Comprehensive 12-lead electrocardiogram for accurate assessment of heart rhythm and electrical activity.',
      benefits: [
        'Comprehensive heart assessment',
        'Non-invasive test',
        'Quick results',
        'Accurate diagnosis'
      ],
      applications: [
        'Heart disease screening',
        'Chest pain evaluation',
        'Pre-surgery assessment',
        'Routine health checkups'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Services
            </h1>
            <p className="text-lg text-gray-600">
              Comprehensive diagnostic services with advanced technology and expert care
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {serviceDetails.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={service.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-gray-600 mb-6">
                      {service.description}
                    </p>

                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">Key Benefits</h3>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">Applications</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {service.applications.map((app, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                            <span className="text-sm text-gray-700">{app}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Why Choose Our Services?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Quick Results
                </h3>
                <p className="text-gray-600">
                  Fast processing and timely delivery of diagnostic reports
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Accurate Reports
                </h3>
                <p className="text-gray-600">
                  Precise and reliable diagnostic results you can trust
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Affordable Pricing
                </h3>
                <p className="text-gray-600">
                  Quality diagnostic services at competitive rates
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Book Your Test?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Contact us now or visit during our working hours
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${labInfo.phone}`}>
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 w-full sm:w-auto">
                Call {labInfo.phone}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-700 w-full sm:w-auto">
                Send Inquiry
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
